<script setup>
import moment from "moment";
import { LayoutDrawer } from "@easy/ui/layouts";
import { CardToggle, CardPreloader } from "@easy/ui/cards";
import { AvatarSm } from "@easy/ui/avatars";
import { TableContainer, TableCell } from "@easy/ui/tables";
import { BadgeStatusSm } from "@easy/ui/badges";
import { CheckIcon, XIcon, LinkShareIcon } from "@easy/ui/icons";
import { useI18n } from "vue-i18n";
import { computed, ref, watch } from "vue";

const props = defineProps({
    user: {
        type: Object,
        required: true,
    },
    lessons: {
        type: Array,
        default: () => [],
    },
    isLoading: {
        type: Boolean,
        default: false,
    },
});
const emit = defineEmits(["hide"]);

const { t } = useI18n();

const openDrawer = ref(true);
watch(openDrawer, (value) => {
    if (!value) {
        emit("hide");
    }
});

const hasLessonsProgress = computed(() => {
    if (props.isLoading) {
        //just to show preloader...
        return true;
    }
    return props.lessons.length > 0;
});
const customerDetails = computed(() => [
    {
        label: t("shared.first_last_name"),
        value: props.user?.fullName,
    },
    {
        label: t("shared.email"),
        value: props.user?.email,
    },
    {
        label: t("player.panel.users.order_date"),
        value: moment(props.user?.orderDate).format("DD.MM.YYYY"),
    },
    {
        label: t("player.panel.users.first_visit"),
        value: props.user?.firstVisitDate
            ? moment(props.user.firstVisitDate).format("DD.MM.YYYY")
            : "-",
    },
    {
        label: t("player.panel.users.last_visit"),
        value: props.user.lastVisitDate
            ? moment(props.user.lastVisitDate).format("DD.MM.YYYY")
            : "-",
    },
    {
        label: t("player.panel.users.number_of_visits"),
        value: props.user.numberOfVisits || "-",
    },
    {
        label: t("player.panel.users.course_progress"),
        value: props.user.courseProgress ? `${Math.round(props.user.courseProgress)}%` : "-",
    },
    /*                {
            label: t("player.panel.users.certificate"),
            value: props.user.hasCertificate,
            isBadge: true,
        },*/
]);
</script>

<template>
    <LayoutDrawer v-model="openDrawer" size="md" :title="user.fullName">
        <template #before-title>
            <AvatarSm image="" :name="user.fullName" />
        </template>
        <template #drawer>
            <CardToggle :title="$t('player.panel.users.customer_data')">
                <TableContainer class="-mt-3">
                    <template #body>
                        <tr v-for="item in customerDetails" :key="item.label">
                            <TableCell>
                                <p class="ct-text-3">
                                    {{ item.label }}
                                </p>
                            </TableCell>
                            <TableCell :wrap-text="true">
                                <BadgeStatusSm
                                    v-if="item.isBadge"
                                    :type="item.value ? 'success' : 'error'"
                                >
                                    <span>
                                        {{
                                            user.certificate
                                                ? $t("player.panel.certificates.yes")
                                                : $t("player.panel.certificates.no")
                                        }}
                                    </span>
                                </BadgeStatusSm>

                                <span v-if="!item.isBadge">{{ item.value }}</span>
                            </TableCell>
                        </tr>
                        <tr>
                            <TableCell :colSpan="2">
                                <a
                                    :href="user.orderUrl"
                                    target="_blank"
                                    class="underline ct-text-3 flex space-x-2 items-center"
                                >
                                    <LinkShareIcon />
                                    <span>{{ $t("player.panel.users.order_details") }}</span>
                                </a>
                            </TableCell>
                        </tr>
                        <tr>
                            <TableCell :colSpan="2">
                                <a
                                    :href="user.customerUrl"
                                    target="_blank"
                                    class="underline ct-text-3 flex space-x-2 items-center"
                                >
                                    <LinkShareIcon />
                                    <span>{{ $t("player.panel.users.customer_details") }}</span>
                                </a>
                            </TableCell>
                        </tr>
                    </template>
                </TableContainer>
            </CardToggle>

            <CardToggle
                :title="$t('player.panel.users.lessons_progress')"
                v-if="hasLessonsProgress"
            >
                <TableContainer class="-mt-3">
                    <template #body>
                        <template v-if="isLoading">
                            <CardPreloader />
                        </template>
                        <template v-else>
                            <tr v-for="lesson in lessons" :key="lesson.name">
                                <TableCell truncate>
                                    <p class="ct-text-3 truncate max-w-72">
                                        {{ lesson.name }}
                                    </p>
                                </TableCell>
                                <TableCell>
                                    <BadgeStatusSm
                                        class="flex-shrink-0"
                                        :type="lesson.done ? 'success' : 'error'"
                                    >
                                        <CheckIcon class="p-1 h-5 w-5" v-if="lesson.done" />
                                        <XIcon class="p-1 h-5 w-5" v-else />
                                    </BadgeStatusSm>
                                </TableCell>
                            </tr>
                        </template>
                    </template>
                </TableContainer>
            </CardToggle>
        </template>
    </LayoutDrawer>
</template>
